import { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  Dialog,
  ErrorState,
  LoadingState,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useVideoAction,
  useVideoSession,
  videoApi,
} from '@/features/video-consults/api';
import {
  BLOCKER_LABEL,
  PAYMENT_LABEL,
  STATUS_LABEL,
  admitBlockedReason,
  isLive,
  accessDisabledReason,
  money,
  readyText,
} from '@/features/video-consults/format';
import type { StaffVideoSession } from '@/features/video-consults/types';
import { klTime } from '@/pages/calendar/time';
import parity from '@/pages/ops/parity.module.css';
import { AfterCallPanel } from './AfterCallPanel';
import { PayLaterButton } from './PayLaterButton';
import { RecordingPanel } from './RecordingPanel';
import { TriageSummary } from './TriageSummary';
import { VideoError } from './VideoError';
import styles from './video.module.css';

// The GetStream SDK is only downloaded when a doctor opens a call.
const CallMedia = lazy(() => import('./call/CallMedia'));

/** The call screen for one session: media on the left, the steps on the right. */
export function VideoCallView({
  id,
  onBack,
}: {
  id: string;
  onBack: () => void;
}) {
  const { hasPermission } = useSession();
  const canManage = hasPermission('teleconsults:manage');
  const q = useVideoSession(id);
  const s = q.data;
  const back = (
    <Button size="sm" variant="ghost" onClick={onBack}>
      ← Back to video consults
    </Button>
  );
  if (q.isLoading) return <LoadingState label="Loading the session…" />;
  if (!s)
    return (
      <>
        {back}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  const st = STATUS_LABEL[s.status];
  const live = isLive(s);
  const blocked = live ? accessDisabledReason(s, canManage) : null;
  return (
    <>
      <div className={styles.head}>
        <div>
          {back}
          <h2 className={styles.headTitle}>
            Video consult ·{' '}
            <Link to={`/patients/${s.patient.id}`}>{s.patient.fullName}</Link>
          </h2>
          <span className={parity.sub}>
            {klTime(s.startsAt)} ·{' '}
            {s.type === 'first_visit' ? 'First visit by video' : 'Follow-up'} ·{' '}
            {s.branch.name}
          </span>
        </div>
        <div className={styles.inline}>
          <Badge tone={st.tone} dot>
            {st.text}
          </Badge>
          <Badge tone={s.payment.state === 'due' ? 'warning' : 'neutral'}>
            {PAYMENT_LABEL[s.payment.state]}
          </Badge>
        </div>
      </div>
      {q.error !== null && (
        <VideoError
          error={q.error}
          title="Could not refresh the session"
          onRetry={() => void q.refetch()}
        />
      )}
      {!live ? (
        <>
          <AfterCallPanel s={s} canManage={canManage && s.mine} />
          <RecordingPanel s={s} />
        </>
      ) : blocked ? (
        <p className={styles.warn} role="status">
          {blocked}
        </p>
      ) : (
        <div className={styles.layout}>
          <Card title="Call">
            <Suspense fallback={<LoadingState label="Loading video…" />}>
              <CallMedia sessionId={s.id} patientName={s.patient.fullName} />
            </Suspense>
          </Card>
          <div className={styles.stack}>
            <SessionSteps s={s} />
            <RecordingPanel s={s} />
          </div>
        </div>
      )}
    </>
  );
}

/** Waiting room -> admit -> identity check -> end (RL-1003), with the blockers. */
function SessionSteps({ s }: { s: StaffVideoSession }) {
  const admit = useVideoAction(videoApi.admit);
  const identity = useVideoAction(videoApi.identityCheck);
  const end = useVideoAction(videoApi.end);
  const [checked, setChecked] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const act = (p: Promise<unknown>) => {
    setError(null);
    p.catch((e: unknown) => setError(e));
  };
  const blockedWhy = admitBlockedReason(s);
  const inCall = s.status === 'admitted' || s.status === 'in_call';
  return (
    <Card title="Steps">
      <div className={styles.stack}>
        {error !== null && <VideoError error={error} />}
        <section aria-label="Before the call" className={styles.stack}>
          <strong>Before the call</strong>
          <TriageSummary triage={s.triage} />
          {s.triage?.guidance && (
            <span className={parity.sub}>{s.triage.guidance.en}</span>
          )}
          <span className={parity.sub}>
            Video consent {s.consent.accepted ? 'accepted' : 'not accepted'} ·
            Fee: {PAYMENT_LABEL[s.payment.state]}
            {s.payment.dueSen > 0 && ` (${money(s.payment.dueSen)} due)`}
          </span>
          {s.blockers.length > 0 && (
            <div className={styles.warn} role="status">
              <strong>The patient cannot join yet:</strong>
              <ul className={styles.blockers}>
                {s.blockers.map((b) => (
                  <li key={b}>{BLOCKER_LABEL[b]}</li>
                ))}
              </ul>
              {s.payment.state === 'due' && <PayLaterButton s={s} />}
            </div>
          )}
        </section>

        <section aria-label="Waiting room" className={styles.stack}>
          <strong>Waiting room</strong>
          {s.status === 'scheduled' && (
            <span className={parity.sub}>
              The patient has not entered the waiting room yet (opens{' '}
              {klTime(s.window.opensAt)}).
            </span>
          )}
          {s.status === 'waiting' && (
            <>
              <span>
                In the waiting room since{' '}
                {s.waitingRoom.since ? klTime(s.waitingRoom.since) : '–'} ·
                camera {readyText(s.waitingRoom.cameraReady)} · microphone{' '}
                {readyText(s.waitingRoom.microphoneReady)}
              </span>
              <div>
                <Button
                  variant="primary"
                  loading={admit.isPending}
                  disabled={blockedWhy !== null}
                  aria-describedby={blockedWhy ? 'admit-blocked' : undefined}
                  onClick={() =>
                    act(admit.mutateAsync({ id: s.id, version: s.version }))
                  }
                >
                  Admit patient
                </Button>
                {blockedWhy && (
                  <span id="admit-blocked" className={styles.reason}>
                    {blockedWhy}
                  </span>
                )}
              </div>
            </>
          )}
          {inCall && s.admittedAt && (
            <span className={parity.sub}>
              Admitted at {klTime(s.admittedAt)}
            </span>
          )}
          {s.rejoinBy && (
            <p className={styles.warn} role="status">
              The patient left the call
              {s.patientLeftAt ? ` at ${klTime(s.patientLeftAt)}` : ''}. They
              can rejoin until {klTime(s.rejoinBy)}; after that the consult ends
              as dropped.
            </p>
          )}
        </section>

        {inCall && (
          <section aria-label="Identity check" className={styles.stack}>
            <strong>Identity check (MMC cl. 11e)</strong>
            {s.status === 'in_call' ? (
              <Badge tone="success">
                Identity checked
                {s.identityCheckedAt
                  ? ` at ${klTime(s.identityCheckedAt)}`
                  : ''}
              </Badge>
            ) : (
              <>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                  />
                  I compared the patient&apos;s face with the MyKad (or
                  passport) held to the camera and with the photo on file.
                </label>
                <div>
                  <Button
                    variant="primary"
                    disabled={!checked}
                    loading={identity.isPending}
                    onClick={() =>
                      act(
                        identity.mutateAsync({ id: s.id, version: s.version }),
                      )
                    }
                  >
                    Record identity check
                  </Button>
                </div>
              </>
            )}
          </section>
        )}

        <div>
          <Button variant="danger" onClick={() => setConfirmEnd(true)}>
            {inCall ? 'End call' : 'Cancel session'}
          </Button>
        </div>
      </div>
      {confirmEnd && (
        <Dialog
          open
          variant="modal"
          title={inCall ? 'End the call for everyone?' : 'Cancel this session?'}
          onClose={() => setConfirmEnd(false)}
          dismissible={!end.isPending}
          footer={
            <>
              <Button onClick={() => setConfirmEnd(false)}>Keep it open</Button>
              <Button
                variant="danger"
                loading={end.isPending}
                onClick={() => {
                  setError(null);
                  end
                    .mutateAsync({ id: s.id, version: s.version })
                    .catch((e: unknown) => setError(e))
                    .finally(() => setConfirmEnd(false));
                }}
              >
                {inCall ? 'End call' : 'Cancel session'}
              </Button>
            </>
          }
        >
          <p className={parity.sub}>
            {inCall
              ? 'The call ends for you and the patient. Record the outcome next.'
              : 'Nobody was let in: the session is cancelled and an unpaid fee bill is voided.'}
          </p>
        </Dialog>
      )}
    </Card>
  );
}
